import type { CSSProperties } from "react";

/** Trimmed href from builder `link` / node `href`, or null when unset. */
export function trimmedElementLinkHref(link?: string | null): string | null {
	if (typeof link !== "string") {
		return null;
	}
	const t = link.trim();
	return t.length > 0 ? t : null;
}

export function elementLinkOpensInNewTab(
	linkTarget?: "_blank" | "_self" | string | null
): boolean {
	return linkTarget === "_blank";
}

export const publishedLinkedElementAnchorStyle: CSSProperties = {
	textDecoration: "none",
	color: "inherit",
};

const TEL_SCHEME = /^tel:/i;
const MAILTO_SCHEME = /^mailto:/i;
const WWW_PREFIX = /^www\./i;
const SLUG_SEPARATORS = /[-_]+/g;

const KNOWN_SOCIAL_HOSTS: ReadonlyArray<readonly [RegExp, string]> = [
	[/facebook\.com$/i, "Facebook"],
	[/instagram\.com$/i, "Instagram"],
	[/(?:youtube\.com|youtu\.be)$/i, "YouTube"],
	[/linkedin\.com$/i, "LinkedIn"],
	[/(?:twitter\.com|x\.com)$/i, "X (Twitter)"],
	[/tiktok\.com$/i, "TikTok"],
	[/pinterest\.[a-z.]+$/i, "Pinterest"],
	[/threads\.net$/i, "Threads"],
	[/(?:t\.me|telegram\.me|telegram\.org)$/i, "Telegram"],
	[/wa\.me$/i, "WhatsApp"],
	[/github\.com$/i, "GitHub"],
];

/**
 * Derive a human-readable accessible name for an icon/image link whose author
 * left the label blank. A nameless link (only a decorative icon inside) is
 * invisible to screen readers and carries no anchor signal, so we never emit
 * one: fall back to a label derived from the destination.
 */
export function deriveIconLinkAccessibleName(href: string): string {
	const raw = href.trim();
	if (!raw) {
		return "Link";
	}
	if (TEL_SCHEME.test(raw)) {
		const num = raw.replace(TEL_SCHEME, "").trim();
		return num ? `Call ${num}` : "Call";
	}
	if (MAILTO_SCHEME.test(raw)) {
		const addr = raw.replace(MAILTO_SCHEME, "").trim();
		return addr ? `Email ${addr}` : "Email";
	}
	try {
		const parsed = new URL(raw, "https://pb.invalid");
		const host = parsed.hostname.replace(WWW_PREFIX, "");
		if (host && host !== "pb.invalid") {
			for (const [pattern, label] of KNOWN_SOCIAL_HOSTS) {
				if (pattern.test(host)) {
					return label;
				}
			}
			return host;
		}
		const segment = parsed.pathname.split("/").filter(Boolean).pop();
		if (segment) {
			const readable = decodeURIComponent(segment)
				.replace(SLUG_SEPARATORS, " ")
				.trim();
			return readable || "Link";
		}
		return "Link";
	} catch {
		return "Link";
	}
}
